import React, { useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { Button, Stack } from '@zet/design-system'
import { useGenerateSharableLink } from '@zet/feature-brands/api/brands/hooks'
import UserConsent from '@zet/feature-brands/components/userConsent'
import { useApplyLink } from '@zet/feature-brands/hooks/useApplyLink'
import { LoggedInNavigation } from '@zet/utils/interface'
import { getName } from '@zet/utils/stringConversion'
import { useForm, useFormState } from 'react-hook-form'

import { convertFormDataToPayload } from '@components/missingFieldsPopup/utils/convertFormDataToPayload'

import { useNavigation } from '@hooks/useNavigation'

import { useSubmitMissingFields } from '../../../../api/missingFieldsPopup/hooks'
import { setFormData } from '../../utils/setFormValues'
import Form from './Form'
import { ajvResolver } from './resolver'

export const FormBuilder = (props) => {
  const { mutate } = useSubmitMissingFields()
  const { name } = useRoute()
  const navigation = useNavigation<LoggedInNavigation>()
  const [consentBottomSheetVisible, setConsentBottomSheetVisible] =
    useState(false)

  const ocFields = props?.ocFields
  const validationSchema = props?.validationSchema || {}
  const fieldValues = props?.fieldValues

  const openConsentBottomSheet = () => {
    setConsentBottomSheetVisible(true)
  }

  const closeConsentBottomSheet = () => {
    setConsentBottomSheetVisible(false)
  }

  const { control, handleSubmit, setValue } = useForm({
    resolver: ajvResolver(validationSchema),
    mode: 'onTouched',
  })

  const { isValid } = useFormState({
    control,
  })
  function handleLeadSource() {
    if (name === 'CrossSelling') {
      return 'cross-web'
    } else return props?.leadSource
  }
  const {
    mutate: fetchShareLink,
    isLoading,
    isSuccess,
    data: shareLinkData,
  } = useGenerateSharableLink()

  const { handleApply } = useApplyLink(
    shareLinkData,
    props?.brandId,
    props?.customerName,
    props?.customerId,
    props?.customerPhoneNumber,
    props?.leadSource,
    openConsentBottomSheet,
    closeConsentBottomSheet
  )

  useEffect(() => {
    if (isSuccess && shareLinkData) {
      handleApply()
    }
  }, [isSuccess, shareLinkData])

  const onSubmit = (data) => {
    publishEvent('click_action', {
      item_name: 'missing_details_form_submit',
      brand_name: props?.brandName,
      partner_id: props?.brandId,
    })
    const convertedData = convertFormDataToPayload(data)
    mutate(
      {
        formData: convertedData?.data,
        customerId: props?.customerId,
      },
      {
        onSuccess: (data) => {
          if (Platform.OS === 'web') {
            fetchShareLink({
              partnerId: Number(props?.brandId),
              customerId: props?.customerId,
              leadSource: handleLeadSource(),
            })
          } else {
            navigation?.replace('ShareCardLink', {
              generateLinkParams: props?.generateLinkParams,
              routeName: 'missingFields',
              brandId: props?.brandId,
              customerName: getName(props?.customerName),
              customerId: props?.customerId,
            })
          }
        },
      }
    )
  }

  useMemo(() => {
    if (ocFields?.length) {
      setFormData(fieldValues, setValue)
    }
  }, [fieldValues, ocFields])

  return (
    <Stack>
      <Form control={control} ocFields={ocFields} />
      <Stack className="px-2 pb-2">
        <Button
          size="lg"
          disabled={!isValid}
          fullWidth
          onPress={handleSubmit(onSubmit)}
          loading={isLoading}
          eventName="click_action"
          eventPayload={{
            item_name: 'missing-details_submit',
            partner_id: props?.brandId,
            customer_id: props?.customerId,
            customer_name: props?.customerName,
            customer_phone_number: props?.customerPhoneNumber,
          }}
        >
          Submit
        </Button>
      </Stack>
      {consentBottomSheetVisible && (
        <UserConsent
          brandId={props?.brandId}
          customerId={props?.customerId}
          isBottomSheetVisible={consentBottomSheetVisible}
          setIsBottomSheetVisible={setConsentBottomSheetVisible}
          customerName={props?.customerName}
          leadSource={props?.leadSource}
          customerPhoneNumber={props?.customerPhoneNumber}
          generateLinkParams={props?.generateLinkParams}
        />
      )}
    </Stack>
  )
}
